import React, { useEffect, useState, useContext, useRef } from "react";
import "./style/ViewFile.scss";

import AttachmentsContext from "../../context/attachmentsContext";
import useDownloadAttachment from "../../hooks/useDownloadAttachment";

import ScriptBox from "../ScriptBox/ScriptBox";
import SmallLoading from "../SmallLoading/SmallLoading";
import DownloadIcon from "../VectorIcons/DownloadIcon";
import FileViewerDialog from "../Globo/FileViewerDialog/FileViewerDialog";

const ViewFile = ({ file }) => {
  const { downloadAttachment, downloadSharedAttachment } =
    useDownloadAttachment();
  const { setClear } = useContext(AttachmentsContext);

  const [localFileURL, setLocalFileURL] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [hasError, setHasError] = useState(false);

  const requestIsDone = useRef(null);
  const localFileURLRef = useRef(null);

  const fileType = fileName?.slice(-3).toLowerCase();

  const modalTitle =
    fileName?.length > 60
      ? fileName?.slice(0, 60) + "..."
      : fileName || "Aguarde...";

  const modalIcon = !!localFileURL ? (
    <div
      className="downloadAction"
      onClick={() => downloadAttachment({ ...file, mode: 0, autoSave: true })}
    >
      <DownloadIcon color="#FFF" />
    </div>
  ) : (
    <></>
  );

  const HandleViewerByFileType = () => {
    if (!localFileURL) return <></>;

    if (["png", "jpeg", "jpg"].includes(fileType)) {
      return <img className="ImgViewer" alt={fileName} src={localFileURL} />;
    }

    if (fileType === "pdf") {
      const hideToolbar = "#toolbar=0";

      return (
        <iframe
          className="PdfViewer"
          src={localFileURL + hideToolbar}
          title={fileName}
          width="100%"
          height="100%"
        />
      );
    }

    if (fileType === "mp3") {
      return (
        <audio
          className="AudioViewer"
          src={localFileURL}
          controls
          style={{ width: "100%" }}
        >
          Seu navegador não suporta o elemento de áudio.
        </audio>
      );
    }

    if (["mp4"].includes(fileType)) {
      return (
        <video
          className="VideoViewer"
          src={localFileURL}
          controls
          style={{ width: "100%", maxHeight: "600px" }}
        >
          Seu navegador não suporta o elemento de vídeo.
        </video>
      );
    }

    return <></>;
  };

  const closeModal = () => setClear();

  const handleDownloadFile = async () => {
    try {
      const response = file?.sharingId
        ? await downloadSharedAttachment({ ...file })
        : await downloadAttachment({ ...file });

      requestIsDone.current = true;

      setFileName(response.fileName);
      const tempUrl = URL.createObjectURL(response.blob);

      localFileURLRef.current = tempUrl;
      setLocalFileURL(tempUrl);
    } catch (error) {
      console.error("Erro ao carregar o arquivo para visualização:", error);
      setHasError(true);
    }
  };

  // the effect below captures a stale localFileURL, so the object URL is revoked from a ref on unmount
  useEffect(
    () => () => {
      if (localFileURLRef.current) URL.revokeObjectURL(localFileURLRef.current);
    },
    []
  );

  useEffect(() => {
    if (!file) return;
    if (file && !requestIsDone.current) handleDownloadFile();

    return () => {
      if (localFileURL) URL.revokeObjectURL(localFileURL);
    };

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, downloadAttachment]);

  if (file?.newView) {
    return (
      <FileViewerDialog
        fileName={fileName}
        fileType={fileType}
        fileUrl={localFileURL}
        isLoading={!localFileURL && !hasError}
        hasError={hasError}
        hideDownload={file?.hideDownload}
        onClose={closeModal}
        onDownload={() =>
          downloadAttachment({ ...file, mode: 0, autoSave: true })
        }
      />
    );
  }

  return (
    <div id="ViewContainer">
      <ScriptBox
        open={true}
        title={modalTitle}
        icon={modalIcon}
        clear={closeModal}
        size="large"
        newHeader={file?.newView}
      >
        <div className="ViewWrapper">
          {!!localFileURL ? <HandleViewerByFileType /> : <SmallLoading />}
        </div>
      </ScriptBox>
    </div>
  );
};

export default ViewFile;
